import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { cn } from '@mlm/ui/lib/cn';
import {
  COACHING_FEELINGS,
  COACHING_FEELING_LABELS,
  type CoachingFeeling,
} from '@mlm/shared/coaching';
import { linkButton } from '../components/panels/today';
import {
  Chip,
  CoachButton,
  ErrorNote,
  Eyebrow,
  Field,
  inputClass,
  Loading,
  SageCheck,
} from '../components/primitives';
import { coaching, type EveningResult, type Today } from '../lib/api';
import { shortDay } from '../lib/format';
import { useRefreshCoaching, useToday } from '../lib/queries';

type Outcome = 'done' | 'partial' | 'pending';
const OUTCOMES: ReadonlyArray<{ value: Outcome; label: string }> = [
  { value: 'done', label: 'Yes' },
  { value: 'partial', label: 'Partially' },
  { value: 'pending', label: 'Not today' },
];

const heading =
  'font-display text-[1.9rem] font-semibold leading-[1.12] text-ink lg:text-[2.5rem] lg:leading-[1.1]';

/** SC-15: close the day. Wins, what got in the way, and tomorrow's #1 priority. */
export function EveningReviewPage() {
  const today = useToday();
  const [result, setResult] = useState<EveningResult | null>(null);
  // The form is tall and its button sits at the bottom, so without this the
  // short result renders above a viewport still parked where the button was:
  // a tester read that as the screen going blank.
  useEffect(() => {
    if (result) window.scrollTo({ top: 0 });
  }, [result]);
  if (today.isPending) return <Loading />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;
  return (
    <Frame>
      {result ? (
        <ReviewResult result={result} />
      ) : today.data.ritual.evening.completed ? (
        <AlreadyClosed today={today.data} />
      ) : (
        <ReviewForm today={today.data} onDone={setResult} />
      )}
    </Frame>
  );
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col px-5 pb-12 pt-6 lg:max-w-[640px] lg:pt-16">
      <div className="mb-8 flex items-center justify-between lg:mb-12">
        <span className="font-display text-lg font-semibold text-ink">Miracles Protocol</span>
        <Link to="/home" className="eyebrow hover:text-ink">
          Close
        </Link>
      </div>
      {children}
    </div>
  );
}

function ClosingPanel() {
  return (
    <div className="flex flex-1 flex-col justify-center" role="status" aria-live="polite">
      <Eyebrow className="mb-3">Evening review</Eyebrow>
      <h1 className={heading}>Closing your day</h1>
      <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70 lg:text-base">
        Master Kala is turning your answers into tomorrow's focus. This takes a few seconds.
      </p>
    </div>
  );
}

function ReviewForm({ today, onDone }: { today: Today; onDone: (r: EveningResult) => void }) {
  const refresh = useRefreshCoaching();
  const primary = today.actions[0] ?? null;
  const [feeling, setFeeling] = useState<CoachingFeeling | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(
    primary?.status === 'done' || primary?.status === 'partial' ? primary.status : null,
  );
  const [wins, setWins] = useState(['', '', '']);
  const [challenge, setChallenge] = useState('');
  const [lesson, setLesson] = useState('');
  const [grateful, setGrateful] = useState('');
  const [tomorrow, setTomorrow] = useState('');
  const [addTomorrow, setAddTomorrow] = useState(true);
  const cleanWins = wins.map((w) => w.trim()).filter(Boolean);
  const tomorrowPriority = tomorrow.trim().length >= 2 ? tomorrow.trim() : undefined;

  const submit = useMutation({
    onMutate: () => window.scrollTo({ top: 0 }),
    mutationFn: async () => {
      if (primary && outcome && outcome !== primary.status) {
        await coaching.updateAction(primary.id, { status: outcome });
      }
      return coaching.eveningReview({
        wins: cleanWins,
        feeling: feeling ?? undefined,
        challenge: challenge.trim() || undefined,
        lesson: lesson.trim() || undefined,
        gratitude: grateful.trim() ? [grateful.trim()] : undefined,
        tomorrowPriority,
        addTomorrowAction: Boolean(tomorrowPriority) && addTomorrow,
      });
    },
    onSuccess: (result) => {
      coaching.event('evening_review_completed', { source: result.source });
      onDone(result);
      void refresh();
    },
  });

  // Writing the summary calls Claude, so the wait is seconds, not milliseconds.
  // The form stays mounted in state terms (these are hooks above the branch),
  // so a failed submit returns the member to everything they typed.
  if (submit.isPending) return <ClosingPanel />;

  return (
    <form
      className="flex flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (cleanWins.length && !submit.isPending) submit.mutate();
      }}
    >
      <Eyebrow className="mb-3">Evening review · {shortDay(today.day)}</Eyebrow>
      <h1 className={heading}>Close your day with intention</h1>
      <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70 lg:text-base">
        A few honest answers. Master Kala turns them into tomorrow's focus.
      </p>

      <fieldset className="mt-10">
        <legend className="eyebrow mb-3">How did today feel?</legend>
        <div className="flex flex-wrap gap-2">
          {COACHING_FEELINGS.map((f) => (
            <Chip
              key={f}
              selected={feeling === f}
              onClick={() => setFeeling(feeling === f ? null : f)}
            >
              {COACHING_FEELING_LABELS[f]}
            </Chip>
          ))}
        </div>
      </fieldset>

      {primary && (
        <fieldset className="mt-9">
          <legend className="eyebrow mb-2">Did you complete your #1 action?</legend>
          <p className="mb-3 text-[0.95rem] leading-snug text-ink">{primary.title}</p>
          <div className="flex flex-wrap gap-2">
            {OUTCOMES.map((o) => (
              <Chip
                key={o.value}
                selected={outcome === o.value}
                onClick={() => setOutcome(o.value)}
              >
                {o.label}
              </Chip>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset className="mt-9 space-y-2.5">
        <legend className="eyebrow mb-3">Today's wins</legend>
        {wins.map((value, i) => (
          <div key={i}>
            <label htmlFor={`review-win-${i}`} className="sr-only">
              Win {i + 1}
            </label>
            <input
              id={`review-win-${i}`}
              className={inputClass}
              maxLength={200}
              value={value}
              placeholder={i === 0 ? 'Something that went well' : 'Another win (optional)'}
              onChange={(e) => setWins(wins.map((w, j) => (j === i ? e.target.value : w)))}
            />
          </div>
        ))}
      </fieldset>

      <div className="mt-9 space-y-6">
        <Field label="What challenged you?" htmlFor="review-challenge">
          <textarea
            id="review-challenge"
            rows={2}
            maxLength={500}
            className={cn(inputClass, 'resize-y')}
            value={challenge}
            onChange={(e) => setChallenge(e.target.value)}
          />
        </Field>
        <Field label="What did you learn?" htmlFor="review-lesson">
          <textarea
            id="review-lesson"
            rows={2}
            maxLength={500}
            className={cn(inputClass, 'resize-y')}
            value={lesson}
            onChange={(e) => setLesson(e.target.value)}
          />
        </Field>
        <Field label="What are you grateful for tonight?" htmlFor="review-gratitude">
          <input
            id="review-gratitude"
            className={inputClass}
            maxLength={200}
            value={grateful}
            onChange={(e) => setGrateful(e.target.value)}
          />
        </Field>
        <Field label="Tomorrow's #1 priority" htmlFor="review-tomorrow">
          <input
            id="review-tomorrow"
            className={inputClass}
            maxLength={200}
            value={tomorrow}
            onChange={(e) => setTomorrow(e.target.value)}
          />
        </Field>
        {tomorrowPriority && (
          <label className="flex min-h-11 items-center gap-3 text-sm text-ink/80">
            <input
              type="checkbox"
              className="h-4 w-4 accent-verde"
              checked={addTomorrow}
              onChange={(e) => setAddTomorrow(e.target.checked)}
            />
            Add it as tomorrow's #1 action
          </label>
        )}
      </div>

      <div className="mt-auto pt-10">
        <CoachButton
          kind="gold"
          type="submit"
          block
          className="lg:w-auto lg:px-11"
          disabled={!cleanWins.length || submit.isPending}
        >
          {submit.isPending ? 'Closing your day' : 'Complete review'}
        </CoachButton>
        {!cleanWins.length && (
          <p className="mt-3 text-xs text-muted-foreground">Add at least one win to finish.</p>
        )}
      </div>
    </form>
  );
}

function ReviewResult({ result }: { result: EveningResult }) {
  return (
    <div className="flex flex-1 flex-col">
      <SageCheck size={28} />
      <h1 className={cn(heading, 'mt-5')}>Your day is closed</h1>
      <p className="mt-4 text-[0.95rem] leading-relaxed text-ink/80 lg:text-base">
        {result.summary}
      </p>
      <section className="mt-8 border-t border-border pt-6">
        <Eyebrow className="mb-2">Your coach says</Eyebrow>
        <p className="text-[0.95rem] leading-relaxed text-ink/80">{result.insight}</p>
      </section>
      {result.tomorrowPriority && (
        <section className="mt-6 border-t border-border pt-6">
          <Eyebrow className="mb-2">Tomorrow's #1 priority</Eyebrow>
          <p className="font-display text-[1.35rem] leading-snug text-ink">
            {result.tomorrowPriority}
          </p>
          {result.tomorrowAction && <p className="eyebrow mt-2">Added to tomorrow's actions</p>}
        </section>
      )}
      <dl className="mt-6 border-t border-border">
        <div className="flex items-baseline justify-between gap-4 border-b border-border py-4">
          <dt className="eyebrow">Today's Success Score</dt>
          <dd className="text-[0.95rem] text-ink">
            {result.score.total} · {result.score.label}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-4 border-b border-border py-4">
          <dt className="eyebrow">Success streak</dt>
          <dd className="text-[0.95rem] text-ink">
            {result.streak.current} {result.streak.current === 1 ? 'day' : 'days'}
          </dd>
        </div>
      </dl>
      <div className="mt-auto pt-10">
        <Link to="/home" className={linkButton}>
          Back to Home
        </Link>
      </div>
    </div>
  );
}

function AlreadyClosed({ today }: { today: Today }) {
  const { summary, tomorrowPriority } = today.ritual.evening;
  return (
    <div className="flex flex-1 flex-col">
      <SageCheck size={28} />
      <h1 className={cn(heading, 'mt-5')}>You closed today</h1>
      {summary && (
        <p className="mt-4 text-[0.95rem] leading-relaxed text-ink/80 lg:text-base">{summary}</p>
      )}
      {tomorrowPriority && (
        <section className="mt-8 border-t border-border pt-6">
          <Eyebrow className="mb-2">Tomorrow's #1 priority</Eyebrow>
          <p className="font-display text-[1.35rem] leading-snug text-ink">{tomorrowPriority}</p>
        </section>
      )}
      <p className="mt-8 text-sm text-muted-foreground">Your next review opens tomorrow.</p>
      <div className="mt-auto pt-10">
        <Link to="/home" className={linkButton}>
          Back to Home
        </Link>
      </div>
    </div>
  );
}
